import { TaxpeerLogo } from "@/components/taxpeerlogo.component";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AuthService from "@/lib/auth/AuthApi.service";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

/**
 * This page is used to change a user's password using a token received via email.
 * The token is extracted from the URL path (/auth/change-password/:token) and validated on page load.
 * If the token is valid, the user can enter a new password.
 * If the token is invalid or expired, an error message is shown.
 */
const ChangePasswordPage = () => {
	const { token: tokenFromURL } = useParams<{ token: string }>();
	const [token, setToken] = useState<string>("");
	const [password, setPassword] = useState<string>("");
	const [confirmPassword, setConfirmPassword] = useState<string>("");
	const [isLoading, setIsLoading] = useState(false);
	const [isValidatingToken, setIsValidatingToken] = useState(true);
	const [tokenValid, setTokenValid] = useState(false);
	const [tokenError, setTokenError] = useState<string>("");
	const navigate = useNavigate();
	const authService = AuthService.getInstance();

	// Extract token from URL and validate it
	useEffect(() => {
		if (!tokenFromURL) {
			setTokenError(
				"Kein Token gefunden. Bitte fordere eine neue Passwort-Reset an.",
			);
			setIsValidatingToken(false);
			return;
		}

		setToken(tokenFromURL);

		// Validate the token
		const validateToken = async () => {
			try {
				const response = await authService.checkTokenValidity(tokenFromURL);
				if (response.isValid) {
					setTokenValid(true);
				} else {
					setTokenError("Der Token ist ungültig oder abgelaufen.");
				}
			} catch (error) {
				setTokenError("Fehler beim Validieren des Tokens.");
			} finally {
				setIsValidatingToken(false);
			}
		};

		validateToken();
	}, [tokenFromURL, authService]);

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		// Clear custom validity message on change
		e.target.setCustomValidity("");
		setPassword(e.target.value);
	};

	const handleConfirmPasswordChange = (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		// Clear custom validity message on change
		e.target.setCustomValidity("");
		setConfirmPassword(e.target.value);
	};

	/**
	 * Sets custom German validation messages for the password input.
	 */
	const handlePasswordInvalid = (e: React.InvalidEvent<HTMLInputElement>) => {
		const input = e.target as HTMLInputElement;
		if (input.validity.valueMissing) {
			input.setCustomValidity("Bitte gebe ein Passwort ein.");
		} else if (input.validity.tooShort) {
			input.setCustomValidity(
				"Das Passwort muss mindestens 8 Zeichen lang sein.",
			);
		} else {
			input.setCustomValidity(""); // Clear message if valid or other error
		}
	};

	/**
	 * Sets custom German validation messages for the confirm password input.
	 */
	const handleConfirmPasswordInvalid = (
		e: React.InvalidEvent<HTMLInputElement>,
	) => {
		const input = e.target as HTMLInputElement;
		if (input.validity.valueMissing) {
			input.setCustomValidity("Bitte bestätige dein Passwort.");
		} else if (password !== confirmPassword) {
			input.setCustomValidity("Die Passwörter stimmen nicht überein.");
		} else {
			input.setCustomValidity(""); // Clear message if valid or other error
		}
	};

	/**
	 * Handles the password change form submission.
	 * Validates that passwords match and then attempts to change the password.
	 */
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Check if passwords match
		if (password !== confirmPassword) {
			const confirmInput = document.getElementById(
				"confirmPassword",
			) as HTMLInputElement;
			confirmInput.setCustomValidity("Die Passwörter stimmen nicht überein.");
			confirmInput.reportValidity();
			return;
		}

		setIsLoading(true);

		try {
			await authService.passwordChangeConfirm({
				token,
				password,
			});

			// Redirect to login page after successful password change
			navigate("/auth/login");
		} catch (error) {
			console.error("Password change failed:", error);
			// The error toast is already shown by the AuthService
		} finally {
			setIsLoading(false);
		}
	};

	// Show loading spinner while validating token
	if (isValidatingToken) {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center bg-card p-4">
				<div className="w-full max-w-[400px] space-y-8">
					<div className="flex justify-center">
						<TaxpeerLogo />
					</div>
					<div className="text-center">
						<h1 className="text-2xl font-semibold text-foreground">
							Token wird überprüft...
						</h1>
						<p className="mt-2 text-muted-foreground">
							Bitte warte einen Moment.
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Show error message if token is invalid
	if (!tokenValid) {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center bg-card p-4">
				<div className="w-full max-w-[400px] space-y-8">
					<div className="flex justify-center">
						<TaxpeerLogo />
					</div>
					<div className="text-center space-y-4">
						<h1 className="text-2xl font-semibold text-foreground">
							Ungültiger Token
						</h1>
						<p className="text-muted-foreground">{tokenError}</p>
						<div className="space-y-2">
							<Link to="/auth/login">
								<Button className="w-full">Zur Anmeldung</Button>
							</Link>
							<p className="text-sm text-muted-foreground">
								Du kannst auf der Anmeldungsseite dein Passwort zurücksetzen.
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Show password change form if token is valid
	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-card p-4">
			<div className="w-full max-w-[400px] space-y-8">
				<div className="flex justify-center">
					<TaxpeerLogo />
				</div>
				{isLoading && (
					<div className="flex justify-center">
						<Loader2 className="animate-spin" />
					</div>
				)}
				{!isLoading && (
					<form onSubmit={handleSubmit} className="space-y-6">
						<h1 className="text-center text-2xl font-semibold text-foreground">
							Neues Passwort festlegen
						</h1>

						<p className="text-center text-sm text-muted-foreground">
							Bitte gebe dein neues Passwort ein.
						</p>

						<div className="space-y-2">
							<label
								className="text-sm text-muted-foreground"
								htmlFor="password"
							>
								Neues Passwort
							</label>
							<Input
								id="password"
								name="password"
								type="password"
								value={password}
								onChange={handlePasswordChange}
								onInvalid={handlePasswordInvalid}
								placeholder="Neues Passwort"
								className="w-full"
								required
								minLength={8}
							/>
							<p className="text-xs text-muted-foreground">
								Mindestens 8 Zeichen
							</p>
						</div>

						<div className="space-y-2">
							<label
								className="text-sm text-muted-foreground"
								htmlFor="confirmPassword"
							>
								Passwort bestätigen
							</label>
							<Input
								id="confirmPassword"
								name="confirmPassword"
								type="password"
								value={confirmPassword}
								onChange={handleConfirmPasswordChange}
								onInvalid={handleConfirmPasswordInvalid}
								placeholder="Passwort bestätigen"
								className="w-full"
								required
								minLength={8}
							/>
						</div>

						<Button
							type="submit"
							className="w-full"
							size="lg"
							disabled={isLoading}
						>
							{isLoading ? "Passwort wird geändert..." : "Passwort ändern"}
						</Button>

						<div className="text-center text-sm">
							<Link
								to="/auth/login"
								className="text-muted-foreground hover:underline"
							>
								Zurück zur Anmeldung
							</Link>
						</div>
					</form>
				)}

				{/* Footer */}
				<div className="flex justify-center space-x-4 text-sm text-muted-foreground">
					<Link to="/agb">AGB</Link>
					<Link to="/datenschutz">Datenschutzerklärung</Link>
				</div>
			</div>
		</div>
	);
};

export default ChangePasswordPage;
